// src/components/dataSells/UploadedFilesList.jsx
import React, { useState } from 'react';
import {
    FaFileAlt,
    FaCheck,
    FaTimes,
    FaExclamationTriangle,
    FaCog,
    FaPlay,
    FaRobot,
    FaEye,
    FaTrash,
    FaSpinner,
    FaCheckCircle,
    FaExclamationCircle
} from 'react-icons/fa';
import FieldMappingModal from './FieldMappingModal';

/**
 * Componente para listar archivos subidos y su estado
 */
const UploadedFilesList = ({
    archivos,
    ventas,
    onValidar,
    onMapearIA,
    onProcesar,
    onEliminar,
    onVerDetalles,
    loading
}) => {
    const [selectedArchivo, setSelectedArchivo] = useState(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [archivoParaMapear, setArchivoParaMapear] = useState(null);

    // Función para contar ventas por archivo
    const contarVentasDeArchivo = (archivoId) => {
        if (!ventas || !Array.isArray(ventas)) return 0;
        return ventas.filter(venta => {
            const archivoOrigenId = venta.archivoOrigen?._id || venta.archivoOrigen;
            return archivoOrigenId === archivoId;
        }).length;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Determinar el estado visual del archivo
    const getEstadoArchivo = (archivo) => {
        const archivoId = archivo.id || archivo._id;
        const numVentas = contarVentasDeArchivo(archivoId);

        // Procesado completamente (verificar primero por ventas en la lista)
        if (numVentas > 0) {
            return {
                icono: <FaCheckCircle className="text-green-500" />,
                texto: `✓ ${numVentas} ventas procesadas`,
                color: 'text-green-600',
                bgColor: 'bg-green-50'
            };
        }

        // Procesado completamente (desde propiedad del archivo)
        if (archivo.ventasProcesadas > 0) {
            return {
                icono: <FaCheckCircle className="text-green-500" />,
                texto: `✓ ${archivo.ventasProcesadas} ventas procesadas`,
                color: 'text-green-600',
                bgColor: 'bg-green-50'
            };
        }

        // Procesamiento automático en curso
        if (archivo.procesamientoAutomatico && !archivo.requiereMapeoManual && !archivo.estadoProcesamiento) {
            return {
                icono: <FaSpinner className="animate-spin text-blue-500" />,
                texto: 'Procesando automáticamente...',
                color: 'text-blue-600',
                bgColor: 'bg-blue-50'
            };
        }

        // Requiere mapeo manual
        if (archivo.requiereMapeoManual) {
            return {
                icono: <FaExclamationCircle className="text-yellow-500" />,
                texto: 'Requiere mapeo manual',
                color: 'text-yellow-600',
                bgColor: 'bg-yellow-50'
            };
        }

        // Error en procesamiento
        if (archivo.estadoProcesamiento === 'error') {
            return {
                icono: <FaTimes className="text-red-500" />,
                texto: 'Error en procesamiento',
                color: 'text-red-600',
                bgColor: 'bg-red-50'
            };
        }

        // Subido, listo para procesar manualmente
        return {
            icono: <FaFileAlt className="text-gray-500" />,
            texto: 'Listo para procesar',
            color: 'text-gray-600',
            bgColor: 'bg-gray-50'
        };
    };

    const getFileIcon = (tipoArchivo) => {
        if (tipoArchivo === 'csv') {
            return <FaFileAlt className="text-green-500 text-2xl" />;
        } else {
            return <FaFileAlt className="text-blue-500 text-2xl" />;
        }
    };

    const getEstadoBadge = (archivo) => {
        const estado = getEstadoArchivo(archivo);
        return (
            <div className={`flex items-center space-x-2 px-3 py-2 ${estado.bgColor} rounded-lg`}>
                {estado.icono}
                <span className={`text-sm font-medium ${estado.color}`}>
                    {estado.texto}
                </span>
            </div>
        );
    };

    const handleMapearManual = (archivo) => {
        setArchivoParaMapear(archivo);
        setShowMappingModal(true);
    };

    if (archivos.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Archivos Subidos</h2>
                <div className="text-center py-12">
                    <FaFileAlt className="mx-auto text-5xl text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay archivos subidos</p>
                    <p className="text-sm text-gray-400 mt-2">
                        Sube tu primer archivo CSV o Excel para comenzar
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Archivos Subidos</h2>

                <div className="space-y-4">
                    {archivos.map(archivo => (
                        <div
                            key={archivo.id || archivo._id}
                            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                        >
                            <div className="flex items-start justify-between">
                                {/* Información del archivo */}
                                <div className="flex items-start space-x-3 flex-1">
                                    <div className="mt-1">
                                        {getFileIcon(archivo.tipoArchivo)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-gray-900 truncate">
                                            {archivo.nombre}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                                            <span>{formatFileSize(archivo.tamañoArchivo)}</span>
                                            <span>•</span>
                                            <span>{formatDate(archivo.fechaSubida)}</span>
                                            <span>•</span>
                                            <span className="capitalize">{archivo.tipoArchivo}</span>
                                        </div>
                                        <div className="mt-2">
                                            {getEstadoBadge(archivo)}
                                        </div>
                                    </div>
                                </div>

                                {/* Acciones */}
                                <div className="flex flex-wrap gap-2 ml-4">
                                    <button
                                        onClick={() => onValidar(archivo.id || archivo._id)}
                                        disabled={loading}
                                        className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex items-center"
                                        title="Validar campos"
                                    >
                                        <FaEye className="mr-1" />
                                        Validar
                                    </button>

                                    <button
                                        onClick={() => onMapearIA(archivo.id || archivo._id)}
                                        disabled={loading}
                                        className="px-3 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex items-center"
                                        title="Mapear con IA"
                                    >
                                        <FaRobot className="mr-1" />
                                        Mapear IA
                                    </button>

                                    <button
                                        onClick={() => handleMapearManual(archivo)}
                                        disabled={loading}
                                        className="px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex items-center"
                                        title="Mapear manualmente"
                                    >
                                        <FaCog className="mr-1" />
                                        Manual
                                    </button>

                                    <button
                                        onClick={() => onProcesar(archivo.id || archivo._id)}
                                        disabled={loading}
                                        className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex items-center"
                                        title="Procesar ventas"
                                    >
                                        <FaPlay className="mr-1" />
                                        Procesar
                                    </button>

                                    <button
                                        onClick={() => onEliminar(archivo.id || archivo._id)}
                                        disabled={loading}
                                        className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex items-center"
                                        title="Eliminar archivo"
                                    >
                                        <FaTrash className="mr-1" />
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal de Mapeo Manual */}
            {showMappingModal && archivoParaMapear && (
                <FieldMappingModal
                    archivo={archivoParaMapear}
                    onClose={() => {
                        setShowMappingModal(false);
                        setArchivoParaMapear(null);
                    }}
                />
            )}
        </>
    );
};

export default UploadedFilesList;

